import React, { useState, useEffect } from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import PropTypes from 'prop-types';

/**
 * Custom Tooltip Component for Joyride
 * Handles close button properly to exit tour
 */
const CustomTooltip = ({ continuous, index, isLastStep, step, backProps, closeProps, primaryProps, skipProps, tooltipProps }) => {
    return (
        <div 
            {...tooltipProps} 
            className="react-joyride__tooltip"
            style={{
                backgroundColor: '#1a1a1a',
                borderRadius: '8px',
                padding: '20px',
                maxWidth: '400px',
                color: '#fff',
                position: 'relative',
                ...tooltipProps?.style
            }}
        >
            {step.title && (
                <h3 style={{ 
                    margin: '0 0 10px 0', 
                    fontSize: '14px', 
                    fontWeight: '600',
                    color: '#fff'
                }}>
                    {step.title}
                </h3>
            )}
            <div style={{ 
                padding: '10px 0',
                color: '#fff',
                fontSize: '14px',
                lineHeight: '1.5'
            }}>
                {step.content}
            </div>
            <footer 
                className="react-joyride__tooltip__footer"
                style={{
                    display: 'flex',
                    gap: '10px',
                    marginTop: '15px',
                    alignItems: 'center'
                }}
            >
                {!isLastStep && (
                    <button 
                        {...skipProps}
                        style={{
                            color: '#999',
                            fontSize: '14px',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px 12px',
                            ...skipProps?.style
                        }}
                    >
                        Skip
                    </button>
                )}
                {index > 0 && (
                    <button 
                        {...backProps}
                        style={{
                            color: '#fff',
                            fontSize: '14px',
                            background: 'transparent',
                            border: '1px solid #666',
                            cursor: 'pointer',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            marginRight: 'auto',
                            ...backProps?.style
                        }}
                    >
                        Back
                    </button>
                )}
                <button 
                    {...primaryProps}
                    style={{
                        backgroundColor: '#29bf00',
                        color: '#000',
                        fontSize: '14px',
                        padding: '10px 20px',
                        borderRadius: '6px',
                        border: 'none',
                        fontWeight: '600',
                        cursor: 'pointer',
                        marginLeft: 'auto',
                        ...primaryProps?.style
                    }}
                >
                    {isLastStep ? 'Finish' : 'Next'}
                </button>
                <button 
                    {...closeProps}
                    style={{ 
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        background: 'transparent',
                        border: 'none',
                        fontSize: '24px',
                        cursor: 'pointer',
                        color: '#fff',
                        padding: '5px 10px',
                        lineHeight: '1',
                        ...closeProps?.style
                    }}
                >
                    ×
                </button>
            </footer>
        </div>
    );
};

/**
 * DashboardTour Component
 * Provides a self-guided tour of the dashboard features
 */
function DashboardTour({ run, onComplete, onSkip }) {
    const [stepIndex, setStepIndex] = useState(0);

    // Reset step index when tour stops
    useEffect(() => {
        if (!run) {
            setStepIndex(0);
        }
    }, [run]);

    const steps = [
        {
            target: '[data-tourid="dashboard-header"]',
            content: (
                <div>
                    <h3>Welcome to Canopy Dashboard!</h3>
                    <p>This is your main control center for monitoring and managing your sensors and actuators.</p>
                </div>
            ),
            placement: 'bottom',
            placementBeacon: 'bottom',
            disableBeacon: true,
            offset: 10,
        },
        {
            target: '[data-tourid="sensor-card"]',
            content: (
                <div>
                    <h3>Sensor Cards</h3>
                    <p>These cards display real-time data from your sensors, including temperature, humidity, and other environmental readings.</p>
                    <p><strong>Click on a sensor card</strong> to view detailed charts, historical data, and manage associated action cards (irrigation, fans, lights, etc.).</p>
                </div>
            ),
            placement: 'auto',
            placementBeacon: 'top',
            disableBeacon: true,
            offset: 10,
        },
        {
            target: '[data-tourid="chatbot-button"]',
            content: (
                <div>
                    <h3>AI Chat Assistant</h3>
                    <p>Get help anytime by clicking the chat icon. Ask questions about your sensors, actions, or get recommendations.</p>
                </div>
            ),
            placement: 'left',
            placementBeacon: 'left',
            disableBeacon: true,
            offset: 15,
        },
        {
            target: '[data-tourid="notifications-button"]',
            content: (
                <div>
                    <h3>Notifications</h3>
                    <p>View alerts and notifications about your sensors and actuators. Stay informed about important events.</p>
                </div>
            ),
            placement: 'left',
            placementBeacon: 'left',
            disableBeacon: true,
            offset: 15,
        },
    ];

    const handleJoyrideCallback = (data) => {
        const { status, type, action, index } = data;

        // Handle close button first - this is the highest priority action
        if (action === ACTIONS.CLOSE) {
            setStepIndex(0);
            if (onSkip) {
                onSkip();
            }
            return;
        }

        // Handle tour completion or skip
        if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
            setStepIndex(0);
            if (onComplete) {
                onComplete();
            }
            if (status === STATUS.SKIPPED && onSkip) {
                onSkip();
            }
            return;
        }

        // Handle step navigation
        if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
            setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
        }
    };

    return (
        <Joyride
            steps={steps}
            run={run}
            stepIndex={stepIndex}
            continuous={true}
            showProgress={true}
            showSkipButton={true}
            callback={handleJoyrideCallback}
            disableScrolling={false}
            disableOverlayClose={true}
            spotlightClicks={false}
            tooltipComponent={(props) => <CustomTooltip {...props} />}
            floaterProps={{
                disableAnimation: false,
                styles: {
                    floater: {
                        filter: 'none',
                    },
                },
                options: {
                    preventOverflow: {
                        boundariesElement: 'viewport',
                    },
                },
            }}
            styles={{
                options: {
                    primaryColor: '#29bf00', // Match your theme color
                    zIndex: 10000,
                    arrowColor: '#1a1a1a',
                    backgroundColor: '#1a1a1a',
                    textColor: '#fff',
                },
                tooltip: {
                    borderRadius: 8,
                    maxWidth: 400,
                    padding: 20,
                },
                tooltipContainer: {
                    textAlign: 'left',
                },
                tooltipContent: {
                    padding: '10px 0',
                },
                buttonNext: {
                    backgroundColor: '#29bf00',
                    color: '#000',
                    fontSize: '14px',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    border: 'none',
                    fontWeight: '600',
                },
                buttonBack: {
                    color: '#fff',
                    fontSize: '14px',
                    marginRight: '10px',
                },
                buttonSkip: {
                    color: '#999',
                    fontSize: '14px',
                },
            }}
            locale={{
                back: 'Back',
                close: 'Close',
                last: 'Finish',
                next: 'Next',
                skip: 'Skip',
            }}
        />
    );
}

DashboardTour.propTypes = {
    run: PropTypes.bool.isRequired,
    onComplete: PropTypes.func,
    onSkip: PropTypes.func,
};

export default DashboardTour;

